import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const DEFAULT_THRESHOLD_PX = 200;

function isNearBottom(
  container: HTMLElement,
  sentinel: Element | null,
  thresholdPx: number,
): boolean {
  if (sentinel) {
    const rootRect = container.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    if (sentinelRect.top <= rootRect.bottom + thresholdPx) return true;
  }

  const { scrollHeight, scrollTop, clientHeight } = container;
  return scrollHeight - scrollTop - clientHeight < thresholdPx;
}

export function useInfiniteScroll(
  sentinelRef: RefObject<Element | null>,
  scrollContainerRef: RefObject<Element | null>,
  fetchNextPage: () => void,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  options?: { thresholdPx?: number; enabled?: boolean; recheckKey?: unknown },
): void {
  const thresholdPx = options?.thresholdPx ?? DEFAULT_THRESHOLD_PX;
  const enabled = options?.enabled ?? true;
  const recheckKey = options?.recheckKey;

  const fetchNextPageRef = useRef(fetchNextPage);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);

  fetchNextPageRef.current = fetchNextPage;
  hasNextPageRef.current = hasNextPage;
  isFetchingNextPageRef.current = isFetchingNextPage;

  const maybeFetchRef = useRef<() => void>(() => {});
  maybeFetchRef.current = () => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPageRef.current || isFetchingNextPageRef.current) return;
    if (isNearBottom(container as HTMLElement, sentinelRef.current, thresholdPx)) {
      fetchNextPageRef.current();
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!enabled || !container) return;

    const maybeFetch = () => maybeFetchRef.current();

    container.addEventListener("scroll", maybeFetch, { passive: true });

    let observer: IntersectionObserver | undefined;
    if (sentinel) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) maybeFetch();
        },
        { root: container, rootMargin: `${thresholdPx}px`, threshold: 0 },
      );
      observer.observe(sentinel);
    }

    const id = requestAnimationFrame(maybeFetch);

    return () => {
      cancelAnimationFrame(id);
      container.removeEventListener("scroll", maybeFetch);
      observer?.disconnect();
    };
  }, [enabled, thresholdPx, scrollContainerRef, sentinelRef, recheckKey]);

  // Collapsing rows or finishing a page fetch can leave the sentinel in view
  // without a scroll event — re-check after those layout changes.
  useEffect(() => {
    if (!enabled || isFetchingNextPage || !hasNextPage) return;
    const id = requestAnimationFrame(() => maybeFetchRef.current());
    return () => cancelAnimationFrame(id);
  }, [enabled, isFetchingNextPage, hasNextPage, recheckKey]);
}
